import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { useApi } from "@/lib/api";

// Default channel surfaced when the page is loaded without an explicit
// `?channel=` query string. For Shomrat Gadol (HERMES_M103, channel 0 = FM-102
// flow meter in m³/h) this is the headline live reading.
const DEFAULT_CHANNEL_INDEX = 0;
const DEFAULT_CHANNEL_NAME = "Flow — FM-102";
const DEFAULT_CHANNEL_UNIT = "m³/h";

const REFETCH_INTERVAL_MS = 15_000;

interface ApiResponse {
  stationId: string;
  stationLabel: string | null;
  lastConnectionTime: string | null;
  rssi: number | null;
  powerSupply: string | null;
  values: Record<string, number>;
  servedFromCache: boolean;
  cacheTtlSeconds: number;
  fetchedAt: string;
}

function formatValue(value: number | undefined): string {
  if (value == null || Number.isNaN(value)) return "—";
  return value.toLocaleString(undefined, {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  });
}

function formatAge(fetchedAt: string | undefined, now: number): string {
  if (!fetchedAt) return "";
  const ageSec = Math.max(0, Math.floor((now - new Date(fetchedAt).getTime()) / 1000));
  if (ageSec < 5) return "just now";
  if (ageSec < 60) return `${ageSec}s ago`;
  const min = Math.floor(ageSec / 60);
  return `${min}m ago`;
}

/**
 * MJOS-187 foundation slice — one live, server-scoped Zeus reading on a page.
 *
 * URL: /zeus/:stationId — the route's stationId is the *requested* station;
 * the server validates it against `client_stations` for the signed-in org
 * before returning data. The browser never holds Zeus credentials.
 *
 * Display contract: headline = the default channel value, in big type, with
 * a "fetched X ago / refetch in Y" line. Errors render plainly. No layout
 * polish — that's the next ticket.
 */
export function StationReadingPage() {
  const { stationId = "" } = useParams<{ stationId: string }>();
  const api = useApi();
  const [now, setNow] = useState(() => Date.now());

  // Drives the relative-time renderer in the absence of a refetch.
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(t);
  }, []);

  const { data, error, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["zeus-last-known-values", stationId],
    queryFn: async (): Promise<ApiResponse> => {
      const res = await api(`/api/zeus/last-known-values/${encodeURIComponent(stationId)}`);
      if (!res.ok) {
        const reason = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status}: ${reason || res.statusText}`);
      }
      return res.json();
    },
    enabled: !!stationId,
    refetchInterval: REFETCH_INTERVAL_MS,
    refetchIntervalInBackground: true,
    staleTime: 0,
  });

  const headline = data?.values?.[String(DEFAULT_CHANNEL_INDEX)];

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6 px-6 py-10">
      <header className="flex flex-col gap-1">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">
          Station {stationId}
        </p>
        <h1 className="text-2xl font-semibold">
          {data?.stationLabel ?? "Loading…"}
        </h1>
        <p className="text-sm text-muted-foreground">
          Live reading from Zeus, scope-checked per client and refreshed every {Math.round(REFETCH_INTERVAL_MS / 1000)}s.
        </p>
      </header>

      {error && (
        <div
          role="alert"
          className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive"
          data-testid="station-reading-error"
        >
          <p className="font-semibold">Could not fetch reading</p>
          <p className="mt-1 break-all">{(error as Error).message}</p>
          <button
            type="button"
            onClick={() => refetch()}
            className="mt-3 rounded-md border border-destructive/40 px-3 py-1.5 text-xs font-semibold hover:bg-destructive/10"
          >
            Try again
          </button>
        </div>
      )}

      <section
        className="rounded-2xl border bg-card px-8 py-10 shadow-sm"
        data-testid="station-reading-headline"
      >
        <p className="text-xs uppercase tracking-wider text-muted-foreground">
          {DEFAULT_CHANNEL_NAME}
        </p>
        <p className="mt-2 flex items-baseline gap-3">
          <span
            className="text-6xl font-semibold tabular-nums tracking-tight"
            data-testid="station-reading-value"
          >
            {isLoading ? "…" : formatValue(headline)}
          </span>
          <span className="text-2xl text-muted-foreground" data-testid="station-reading-unit">
            {DEFAULT_CHANNEL_UNIT}
          </span>
        </p>
        <p className="mt-3 text-xs text-muted-foreground" data-testid="station-reading-age">
          {data?.fetchedAt
            ? `Fetched ${formatAge(data.fetchedAt, now)}${data.servedFromCache ? " · served from cache" : ""}`
            : isFetching
              ? "Fetching…"
              : ""}
        </p>
      </section>

      {data && (
        <section className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
          <Stat label="Station ID" value={data.stationId} />
          <Stat label="Last connection" value={data.lastConnectionTime ?? "—"} />
          <Stat label="RSSI" value={data.rssi != null ? String(data.rssi) : "—"} />
          <Stat label="Power" value={data.powerSupply ?? "—"} />
        </section>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border bg-muted/30 px-3 py-2">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="mt-1 break-all text-sm font-medium">{value}</p>
    </div>
  );
}
